import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { fleetLabelMaps, categoryLabel } from "../../lib/vehicleLabel";
import {
  Loader2, Sparkles, CalendarCheck, Gauge, Car, AlertTriangle, Users, Clock,
  Package, CheckCircle2, XCircle, Hourglass, UserPlus, CircleParking, Wrench, BarChart3, SlidersHorizontal,
} from "lucide-react";
import { FilterDropdown } from "../../components/pilotage/Filters";
import SegmentedPills from "../../components/ui/SegmentedPills";
import { SkeletonDashboard } from "../../components/ui/Skeleton";
import { StatsKpiCard, StatsBadge } from "../../components/stats/StatsKpiCard";
import { ChartCard, ActivityChart, TopVehiclesChart, CategoryChart, OptionsDonut, LocationsChart } from "../../components/stats/StatsCharts";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const PERIODS = [
  { key: "7d", label: "7 jours" },
  { key: "30d", label: "30 jours" },
  { key: "90d", label: "90 jours" },
  { key: "month", label: "Mois en cours" },
  { key: "year", label: "Année" },
];

function periodDates(key) {
  const today = new Date();
  const to = ymd(today);
  if (key === "month") return { from: ymd(new Date(today.getFullYear(), today.getMonth(), 1)), to };
  if (key === "year") return { from: ymd(new Date(today.getFullYear(), 0, 1)), to };
  const days = key === "7d" ? 6 : key === "90d" ? 89 : 29;
  const s = new Date(today); s.setDate(s.getDate() - days);
  return { from: ymd(s), to };
}

const fmtFR = (iso) => { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; };

function buildCopilotPrompt(data, filterLabels) {
  const k = data.kpis, v = data.variations;
  const lines = [
    "Analyse mon tableau de bord Statistiques (activité de location) :",
    `📅 Période : du ${fmtFR(data.period.from)} au ${fmtFR(data.period.to)} (${data.period.days} jours)${filterLabels.length ? ` — Filtres : ${filterLabels.join(", ")}` : ""}`,
    `📊 Réservations : ${k.bookings_period} sur la période (${v.bookings_period > 0 ? "+" : ""}${v.bookings_period}% vs période précédente) — ${k.confirmed} confirmées, ${k.pending} en attente, ${k.cancelled} annulées, ${k.completed} terminées`,
    `🚗 Flotte : ${k.fleet_total} véhicules — ${k.fleet_rented} loués, ${k.fleet_available} disponibles, ${k.fleet_immobilized} immobilisés`,
    `📈 Taux d'occupation : ${k.occupancy}% (${v.occupancy > 0 ? "+" : ""}${v.occupancy} pts) — Durée moyenne de location : ${k.avg_duration} jours`,
    `👥 Clients : ${k.active_clients} actifs, ${k.new_clients} nouveaux — Options vendues : ${k.options_sold}`,
  ];
  if (k.late_returns > 0) lines.push(`⚠️ ${k.late_returns} restitution(s) en retard`);
  const badges = Object.values(data.badges || {});
  if (badges.length) lines.push(`🔔 Signaux détectés : ${badges.map((b) => `${b.icon} ${b.label} (${b.detail})`).join(" ; ")}`);
  const top = (data.top_vehicles || []).slice(0, 3);
  if (top.length) lines.push(`🏆 Top véhicules : ${top.map((t) => `${t.name} (${t.occupancy}%)`).join(", ")}`);
  lines.push("Analyse ces données, identifie les tendances marquantes et les risques, puis propose-moi 3 actions concrètes pour améliorer mon taux d'occupation.");
  return lines.join("\n");
}

export default function VendorStatistics({ onAnalyzeWithCopilot }) {
  const [, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [locationFilter, setLocationFilter] = useState("all");
  const [vehicleFilter, setVehicleFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilterCount = [locationFilter, vehicleFilter, categoryFilter, statusFilter].filter((x) => x !== "all").length;

  const [vehicles, setVehicles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [catMap, setCatMap] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/vendor/vehicles"), api.get("/vendor/locations"), api.get("/vendor/settings").catch(() => ({ data: {} }))]).then(([v, l, s]) => {
      setVehicles(v.data.vehicles || []);
      setLocations(l.data.locations || []);
      setCatMap(fleetLabelMaps(s.data?.fleet || {}).catMap);
    });
  }, []);

  const dates = useMemo(() => {
    if (period === "custom" && customFrom && customTo) return { from: customFrom, to: customTo };
    return periodDates(period === "custom" ? "30d" : period);
  }, [period, customFrom, customTo]);

  useEffect(() => {
    setLoading(true);
    const params = { date_from: dates.from, date_to: dates.to };
    if (locationFilter !== "all") params.location_id = locationFilter;
    if (vehicleFilter !== "all") params.vehicle_id = vehicleFilter;
    if (categoryFilter !== "all") params.category = categoryFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/vendor/stats/dashboard", { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [dates.from, dates.to, locationFilter, vehicleFilter, categoryFilter, statusFilter]);

  const categories = useMemo(() => [...new Set(vehicles.map((v) => v.category).filter(Boolean))], [vehicles]);

  const filterLabels = useMemo(() => {
    const out = [];
    if (locationFilter !== "all") out.push(`agence ${locations.find((l) => l.id === locationFilter)?.name || ""}`);
    if (vehicleFilter !== "all") { const v = vehicles.find((x) => x.id === vehicleFilter); out.push(`véhicule ${v ? `${v.brand} ${v.model}` : ""}`); }
    if (categoryFilter !== "all") out.push(`catégorie ${categoryLabel(categoryFilter, catMap) || categoryFilter}`);
    if (statusFilter !== "all") out.push(`statut ${statusFilter}`);
    return out;
  }, [locationFilter, vehicleFilter, categoryFilter, statusFilter, locations, vehicles, catMap]);

  const goTo = (params) => setSearchParams(params);

  const analyze = () => {
    if (!data) return;
    onAnalyzeWithCopilot?.(buildCopilotPrompt(data, filterLabels));
  };

  const k = data?.kpis, v = data?.variations, badges = data?.badges || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="stats-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
            <span className="h-8 w-8 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center"><BarChart3 className="h-4.5 w-4.5" style={{ height: 18, width: 18 }} /></span>
            Statistiques
          </h1>
          <p className="text-xs text-slate-500 mt-1">Activité de location de votre flotte — indicateurs opérationnels, hors données financières.</p>
        </div>
        <button
          onClick={analyze}
          disabled={!data}
          className="inline-flex items-center gap-2 h-10 px-3 sm:px-4 rounded-xl bg-gradient-to-r from-violet-600 to-violet-500 text-white text-[13px] font-semibold shadow-[0_4px_14px_-4px_rgba(124,58,237,0.5)] hover:from-violet-700 hover:to-violet-600 transition-colors disabled:opacity-50"
          data-testid="stats-analyze-copilot-btn"
        >
          <Sparkles className="h-4 w-4" /> <span className="hidden sm:inline">Analyser avec Junago Copilot</span><span className="sm:hidden">Analyser</span>
        </button>
      </div>

      {/* Filtres globaux */}
      <div data-testid="stats-filters" className="space-y-2">
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
          <SegmentedPills items={PERIODS} value={period === "custom" ? "" : period} onChange={setPeriod} testid="stats-period" />
          <button
            onClick={() => setFiltersOpen((o) => !o)}
            className={`lg:hidden shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border text-xs font-semibold transition-colors ${filtersOpen || activeFilterCount > 0 ? "border-violet-300 bg-violet-50 text-violet-700" : "border-slate-200 bg-white text-slate-600"}`}
            data-testid="stats-filters-toggle"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" /> Filtres
            {activeFilterCount > 0 && <span className="min-w-[16px] h-4 px-1 rounded-full bg-violet-600 text-white text-[10px] font-bold flex items-center justify-center">{activeFilterCount}</span>}
          </button>
        </div>
        <div className={`${filtersOpen ? "flex" : "hidden"} lg:flex flex-wrap items-center gap-2`}>
          <div className="flex items-center gap-1.5">
            <input type="date" value={customFrom} onChange={(e) => { setCustomFrom(e.target.value); if (e.target.value && customTo) setPeriod("custom"); }}
              className="h-8 px-2 rounded-xl border border-slate-200 text-xs text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-500" data-testid="stats-date-from" />
            <span className="text-[10px] text-slate-400">→</span>
            <input type="date" value={customTo} onChange={(e) => { setCustomTo(e.target.value); if (customFrom && e.target.value) setPeriod("custom"); }}
              className="h-8 px-2 rounded-xl border border-slate-200 text-xs text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-500" data-testid="stats-date-to" />
          </div>
          {locations.length > 1 && (
            <FilterDropdown label="Agence" value={locationFilter} onChange={setLocationFilter}
              options={[{ value: "all", label: "Toutes les agences" }, ...locations.map((l) => ({ value: l.id, label: l.name }))]} testid="stats-location-filter" />
          )}
          <FilterDropdown label="Véhicule" value={vehicleFilter} onChange={setVehicleFilter}
            options={[{ value: "all", label: "Tous les véhicules" }, ...vehicles.map((x) => ({ value: x.id, label: `${x.brand} ${x.model}` }))]} testid="stats-vehicle-filter" />
          {categories.length > 0 && (
            <FilterDropdown label="Catégorie" value={categoryFilter} onChange={setCategoryFilter}
              options={[{ value: "all", label: "Toutes catégories" }, ...categories.map((c) => ({ value: c, label: categoryLabel(c, catMap) || "Sans catégorie" }))]} testid="stats-category-filter" />
          )}
          <FilterDropdown label="Statut" value={statusFilter} onChange={setStatusFilter}
            options={[
              { value: "all", label: "Tous les statuts" },
              { value: "confirmed", label: "Confirmées" },
              { value: "pending", label: "En attente" },
              { value: "completed", label: "Terminées" },
              { value: "cancelled", label: "Annulées" },
            ]} testid="stats-status-filter" />
        </div>
      </div>

      {loading || !data ? (
        <SkeletonDashboard testid="stats-skeleton" />
      ) : (
        <>
          {/* Badges IA locaux */}
          {Object.keys(badges).length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="stats-badges">
              {Object.entries(badges).map(([key, b]) => <StatsBadge key={key} badge={b} testid={`stats-badge-${key}`} />)}
            </div>
          )}

          {/* KPIs principaux */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="stats-kpis-primary">
            <StatsKpiCard label={`Réservations (${data.period.days} j)`} value={k.bookings_period} icon={CalendarCheck} variation={v.bookings_period}
              badge={badges.bookings_period} onClick={() => goTo({ tab: "bookings" })} testid="stats-kpi-bookings-period" />
            <StatsKpiCard label="Taux d'occupation" value={k.occupancy} unit="%" icon={Gauge} accent="emerald" variation={v.occupancy} variationSuffix=" pts"
              badge={badges.occupancy} onClick={() => goTo({ tab: "calendar" })} testid="stats-kpi-occupancy" />
            <StatsKpiCard label="Véhicules disponibles" value={k.fleet_available} sub={`sur ${k.fleet_total} au total`} icon={Car} accent="sky"
              badge={badges.fleet} onClick={() => goTo({ tab: "vehicles" })} testid="stats-kpi-available" />
            <StatsKpiCard label="Retards de restitution" value={k.late_returns} icon={AlertTriangle} accent={k.late_returns > 0 ? "red" : "slate"}
              badge={badges.late} onClick={() => goTo({ tab: "bookings" })} testid="stats-kpi-late" />
          </div>

          {/* KPIs secondaires */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stats-kpis-secondary">
            <StatsKpiCard label="Résas aujourd'hui" value={k.bookings_today} icon={Clock} accent="slate" onClick={() => goTo({ tab: "bookings" })} testid="stats-kpi-today" />
            <StatsKpiCard label="Cette semaine" value={k.bookings_week} icon={CalendarCheck} accent="slate" onClick={() => goTo({ tab: "bookings" })} testid="stats-kpi-week" />
            <StatsKpiCard label="Ce mois" value={k.bookings_month} icon={CalendarCheck} accent="slate" onClick={() => goTo({ tab: "bookings" })} testid="stats-kpi-month" />
            <StatsKpiCard label="Confirmées" value={k.confirmed} icon={CheckCircle2} accent="emerald" onClick={() => goTo({ tab: "bookings" })} testid="stats-kpi-confirmed" />
            <StatsKpiCard label="En attente" value={k.pending} icon={Hourglass} accent="amber" onClick={() => goTo({ tab: "bookings" })} testid="stats-kpi-pending" />
            <StatsKpiCard label="Annulées" value={k.cancelled} icon={XCircle} accent={k.cancelled > 0 ? "red" : "slate"} variation={v.cancelled}
              badge={badges.cancelled} onClick={() => goTo({ tab: "bookings" })} testid="stats-kpi-cancelled" />
          </div>

          {/* Flotte & clients */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stats-kpis-fleet">
            <StatsKpiCard label="Véhicules loués (auj.)" value={k.fleet_rented} icon={Car} accent="violet" onClick={() => goTo({ tab: "vehicles" })} testid="stats-kpi-rented" />
            <StatsKpiCard label="Immobilisés" value={k.fleet_immobilized} icon={Wrench} accent={k.fleet_immobilized > 0 ? "amber" : "slate"} onClick={() => goTo({ tab: "vehicles", view: "events" })} testid="stats-kpi-immobilized" />
            <StatsKpiCard label="Durée moyenne" value={k.avg_duration} unit="jours" icon={CircleParking} accent="sky" onClick={() => goTo({ tab: "bookings" })} testid="stats-kpi-duration" />
            <StatsKpiCard label="Clients actifs" value={k.active_clients} icon={Users} accent="violet" onClick={() => goTo({ tab: "clients" })} testid="stats-kpi-clients" />
            <StatsKpiCard label="Nouveaux clients" value={k.new_clients} icon={UserPlus} accent="emerald" onClick={() => goTo({ tab: "clients" })} testid="stats-kpi-new-clients" />
            <StatsKpiCard label="Options vendues" value={k.options_sold} icon={Package} accent="amber" variation={v.options_sold}
              badge={badges.options} onClick={() => goTo({ tab: "bookings" })} testid="stats-kpi-options" />
          </div>

          {/* Graphiques */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ChartCard title="Activité de la période" subtitle="Réservations créées, annulations et taux d'occupation" testid="stats-chart-activity" className="lg:col-span-2">
              <ActivityChart series={data.series} />
            </ChartCard>
            <ChartCard title="Occupation par véhicule" subtitle="Top véhicules — cliquez pour ouvrir la fiche" testid="stats-chart-vehicles">
              {data.top_vehicles.length ? (
                <TopVehiclesChart data={data.top_vehicles} onVehicleClick={(id) => goTo({ tab: "vehicles", vehicle: id })} />
              ) : <EmptyChart />}
            </ChartCard>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ChartCard title="Par catégorie" subtitle="Réservations et occupation par catégorie de véhicule" testid="stats-chart-categories">
              {data.occupancy_by_category.length ? <CategoryChart data={data.occupancy_by_category} /> : <EmptyChart />}
            </ChartCard>
            <ChartCard title="Options les plus réservées" subtitle="Répartition des options sur la période" testid="stats-chart-options">
              {data.options_distribution.length ? <OptionsDonut data={data.options_distribution} /> : <EmptyChart />}
            </ChartCard>
            <ChartCard title="Réservations par agence" subtitle="Lieu de départ des locations" testid="stats-chart-locations">
              {data.top_locations.length ? <LocationsChart data={data.top_locations} /> : <EmptyChart />}
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
}

function EmptyChart() {
  return <div className="h-40 flex items-center justify-center text-xs text-slate-400">Aucune donnée sur la période</div>;
}
